import { useState, useEffect, useRef, useCallback } from 'react';
import API_URL from '../config';
import logger from '../utils/logger';

// Máximo de tempo aguardando resposta da IA antes de desistir (ms).
// #216: 6 minutos — a Investigação real leva 130-180s sob cold-start do
// backend; o timeout de 2min desistia de análises que iam dar certo.
const POLLING_TIMEOUT_MS = 360_000;

// Quantas falhas CONSECUTIVAS de poll (404 de tarefa perdida, rede, 5xx) toleramos
// antes de encerrar com um aviso — em vez de girar o spinner pra sempre. 401/403
// (sessão expirada) é terminal IMEDIATO (não conta pra cá).
const MAX_CONSECUTIVE_ERRORS = 5;

/**
 * Hook para acompanhar status de análise assíncrona.
 * Garante que o polling pare após POLLING_TIMEOUT_MS mesmo se a task
 * nunca sair do estado "processing" (proteção contra travar infinitamente).
 *
 * @param {number} ncId - ID da NC
 * @param {boolean} enabled - Se deve fazer polling
 * @param {number} pollInterval - Intervalo de polling em ms (padrão: 2000)
 * @returns {object} { status, progress, result, error, isLoading, elapsedSeconds, cancel, refetch }
 */
export function useAnalysisStatus(ncId, enabled = false, pollInterval = 2000) {
  const [status, setStatus] = useState('idle'); // idle | pending | processing | completed | failed | cancelled | timeout
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Ref para o timestamp em que o polling começou — usado para o timeout máximo
  const startedAtRef = useRef(null);
  // Contador de falhas CONSECUTIVAS do poll (zera a cada 200 ok).
  const consecutiveErrorsRef = useRef(0);

  // #216: timeout do POLL ≠ falha da análise — o servidor pode estar só
  // demorando. Status próprio pra UI distinguir "desisti de esperar" de
  // "o backend reportou erro".
  const stopPolling = useCallback((reason) => {
    setStatus('timeout');
    setError(reason);
    setResult(null);
  }, []);

  // Buscar status da análise
  const fetchStatus = useCallback(async () => {
    if (!ncId || !enabled) return;

    // Checar se já passou do timeout máximo
    if (startedAtRef.current) {
      const elapsed = Date.now() - startedAtRef.current;
      if (elapsed > POLLING_TIMEOUT_MS) {
        stopPolling(
          'A análise está demorando mais que o normal. Ela continua sendo ' +
          'processada no servidor — você pode sair desta página e voltar ' +
          'pela lista de NCs.'
        );
        return;
      }
    }

    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await fetch(
        `${API_URL}/api/v2/nc/${ncId}/analise-status`,
        { headers }
      );

      if (!response.ok) {
        // 401/403: a sessão expirou DURANTE a espera. O poll usa fetch cru (fora do
        // interceptor de refresh do axios), então o 401 não é tratado em lugar nenhum
        // → antes o spinner girava pra sempre. Agora vira estado TERMINAL próprio.
        if (response.status === 401 || response.status === 403) {
          setStatus('session_expired');
          setError('Sua sessão expirou. Faça login novamente — a análise pode ter continuado no servidor; ela aparece na lista de NCs quando terminar.');
          setResult(null);
          return;
        }
        // 404 = nenhuma análise em progresso (ex.: tarefa perdida num restart do
        // backend). Pode ser transitório logo após o disparo; conta como falha e o
        // backstop encerra se persistir.
        if (response.status === 404) {
          consecutiveErrorsRef.current += 1;
          if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS) {
            stopPolling('Não encontramos a análise em andamento — ela pode ter sido interrompida no servidor. Verifique o resultado ou reanalise pela lista de NCs.');
          } else {
            setStatus('idle');
          }
          return;
        }
        throw new Error(`Erro ao buscar status: ${response.status}`);
      }

      const data = await response.json();
      consecutiveErrorsRef.current = 0;  // sucesso → zera o contador de falhas

      setStatus(data.status);
      setProgress(data.progress || 0);
      setElapsedSeconds(data.elapsed_seconds || 0);

      // Log de polling
      logger.logPolling(data.status, data.progress || 0, ncId, data.error || null);

      if (data.status === 'completed') {
        setResult(data.result);
        setError(null);
        logger.info('Analysis status: completed', { ncId, progress: data.progress });
      } else if (data.status === 'failed') {
        setError(
          data.error
            ? `Reanálise falhou: ${data.error}`
            : 'A reanálise falhou. Tente novamente ou contate o suporte.'
        );
        setResult(null);
        logger.error('Analysis status: failed', { ncId, error: data.error });
      } else if (data.status === 'cancelled') {
        setError('Análise cancelada pelo usuário.');
        setResult(null);
        logger.warn('Analysis status: cancelled', { ncId });
      }
    } catch (err) {
      // Falha de rede / 5xx / parse. Conta como erro consecutivo; se persistir,
      // encerra com aviso em vez de girar pra sempre.
      consecutiveErrorsRef.current += 1;
      logger.error('Error fetching analysis status', { ncId, error: err.message });
      console.error('Erro ao buscar status da análise:', err);
      if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS) {
        stopPolling('Não conseguimos acompanhar a análise (conexão ou sessão instável). Ela pode ter continuado no servidor — verifique o resultado ou reanalise pela lista de NCs.');
      } else {
        setError(err.message);
      }
    }
  }, [ncId, enabled, stopPolling]);

  // Polling automático — para automaticamente quando status é terminal ou timeout
  useEffect(() => {
    if (!enabled || !ncId) return;

    // Registrar início do polling
    startedAtRef.current = Date.now();

    // Buscar imediatamente
    fetchStatus();

    const interval = setInterval(() => {
      // Parar se atingiu status terminal
      if (['completed', 'failed', 'cancelled', 'timeout', 'session_expired'].includes(status)) {
        clearInterval(interval);
        return;
      }
      fetchStatus();
    }, pollInterval);

    return () => {
      clearInterval(interval);
      startedAtRef.current = null;
    };
  }, [ncId, enabled, pollInterval, fetchStatus, status]);

  // Cancelar análise
  const cancel = useCallback(async () => {
    if (!ncId) return;
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const response = await fetch(
        `${API_URL}/api/v2/nc/${ncId}/cancelar-analise`,
        { method: 'POST', headers }
      );
      if (!response.ok) throw new Error(`Erro ao cancelar: ${response.status}`);
      setStatus('cancelled');
      setError('Análise cancelada.');
    } catch (err) {
      console.error('Erro ao cancelar análise:', err);
      setError(err.message);
    }
  }, [ncId]);

  return {
    status,
    progress,
    result,
    error,
    isLoading: status === 'processing' || status === 'pending',
    elapsedSeconds,
    cancel,
    refetch: fetchStatus,
  };
}

export default useAnalysisStatus;
